import React, { useState, useEffect, useRef, useCallback } from 'react';
import './InlineEditor.css';
import QuizEditor from './QuizEditor';
import { useImageRegeneration } from './useImageRegeneration';
import ImageRegenPanel from './ImageRegenPanel';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

const getAuthToken = (propToken) => {
  if (propToken) return propToken;
  try {
    const raw = localStorage.getItem('app_auth_session');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.session_token === 'string' ? parsed.session_token.trim() : '';
  } catch { return ''; }
};

const authHeaders = (propToken) => {
  const t = getAuthToken(propToken);
  const h = { 'Content-Type': 'application/json' };
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
};

const toRelativeSrc = (src) => {
  if (!src) return src;
  try {
    const url = new URL(src, window.location.origin);
    return url.pathname + (url.search || '');
  } catch { return src; }
};

const sendToIframe = (iframeRef, cmd, extra = {}) => {
  try {
    iframeRef.current?.contentWindow?.postMessage(
      { __inlineEditCmd: cmd, ...extra },
      '*'
    );
  } catch (e) {
    console.warn('[InlineEditor] postMessage failed:', e);
  }
};

export default function InlineEditor({ templateId, isOwner, isEditMode, setIsEditMode, iframeRef, token }) {
  const [pendingEdits, setPendingEdits] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [, setListenerReady] = useState(false);
  const saveTimerRef = useRef(null);
  const [quizRequest, setQuizRequest] = useState(null);

  // Slide insert state
  const [slideInsertRequest, setSlideInsertRequest] = useState(null);
  const [slideInsertStatus, setSlideInsertStatus] = useState('idle');
  const [slideInsertError, setSlideInsertError] = useState('');
  const [slideInsertPrompt, setSlideInsertPrompt] = useState('');
  const [slideInsertType, setSlideInsertType] = useState('content');
  const [slideInsertModel, setSlideInsertModel] = useState('claude');

  const { regen, start: startRegen, retry: retryRegen, accept: acceptRegen, cancel: cancelRegen } =
    useImageRegeneration(templateId, token);

  const [imgOverlays, setImgOverlays] = useState([]);

  const requestImageOverlays = useCallback(() => {
    if (!isEditMode) return;
    sendToIframe(iframeRef, 'REQUEST_IMAGE_OVERLAYS');
  }, [isEditMode, iframeRef]);

  const reinjectListener = useCallback(async () => {
    if (!templateId) return;
    try {
      await fetch(
        `${API_URL}/lesson/templates/${templateId}/reinject-listener`,
        { method: 'POST', credentials: 'include', headers: authHeaders(token) }
      );
    } catch (e) {
      console.warn('[InlineEditor] reinject failed:', e);
    }
  }, [templateId]);

  useEffect(() => {
    const onMessage = (e) => {
      if (!e.data) return;

      if (e.data.type === 'INLINE_EDIT_LISTENER_READY') {
        setListenerReady(true);
        if (isEditMode) sendToIframe(iframeRef, 'ACTIVATE');
      }

      if (e.data.type === 'QUIZ_EDIT_REQUEST') {
        setQuizRequest({
          section_id: e.data.section_id,
          quiz_type: e.data.quiz_type,
          assessment_index: e.data.assessment_index,
        });
      }

      if (e.data.type === 'INLINE_EDIT_READY') {
        requestImageOverlays();
      }

      if (e.data.type === 'INLINE_EDIT_UPDATE') {
  const incoming = e.data.edits || [];
  setPendingEdits(prev => {
    // Merge: incoming edits override existing ones with same id, keep others
    const incomingIds = new Set(incoming.map(e => e.id));
    const kept = prev.filter(e => !incomingIds.has(e.id));
    return [...kept, ...incoming];
  });
}

      if (e.data.type === 'SLIDE_INSERT_REQUEST') {
        console.log('[SlideInsert] received in parent', e.data); 
        setSlideInsertRequest({
          afterPageId: e.data.afterPageId,
          prevSlideText: e.data.prevSlideText,
          nextSlideText: e.data.nextSlideText,
        });
        setSlideInsertPrompt('');
        setSlideInsertStatus('idle');
        setSlideInsertError('');
        setSlideInsertType('content');
        setSlideInsertModel('claude');
      }

      if (e.data.type === 'IMAGE_OVERLAYS') {
        if (!iframeRef?.current) return;
        const iframeRect = iframeRef.current.getBoundingClientRect();
        const iframeScrollTop  = e.data.scrollY ?? 0;
        const iframeScrollLeft = e.data.scrollX ?? 0;

        const adjusted = (e.data.overlays || [])
          .map(o => {
            const rawTop  = iframeRect.top  + (o.top  - iframeScrollTop);
            const rawLeft = iframeRect.left + (o.left - iframeScrollLeft);
            const clampedTop  = Math.min(Math.max(rawTop,  iframeRect.top  + 8), iframeRect.top  + iframeRect.height - 50);
            const clampedLeft = Math.min(Math.max(rawLeft, iframeRect.left + 8), iframeRect.left + iframeRect.width  - 50);
            return {
              ...o,
              src:    toRelativeSrc(o.src),
              top:    clampedTop,
              left:   clampedLeft,
              width:  o.width,
              height: o.height,
            };
          })
          .filter(o => o.width > 20 && o.height > 20)
          .filter(o => {
            const visibleTop    = Math.max(o.top, iframeRect.top);
            const visibleBottom = Math.min(o.top + o.height, iframeRect.top + iframeRect.height);
            const visibleLeft   = Math.max(o.left, iframeRect.left);
            const visibleRight  = Math.min(o.left + o.width, iframeRect.left + iframeRect.width);
            return (visibleBottom - visibleTop) > o.height * 0.3 && (visibleRight - visibleLeft) > o.width * 0.3;
          });

        setImgOverlays(adjusted);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isEditMode, iframeRef, requestImageOverlays]);

  useEffect(() => {
    if (isEditMode) {
      reinjectListener().then(() => {
        setTimeout(() => sendToIframe(iframeRef, 'ACTIVATE'), 400);
      });
    } else {
      sendToIframe(iframeRef, 'DEACTIVATE');
      setPendingEdits([]);
      setSaveStatus('');
      setQuizRequest(null);
      setImgOverlays([]);
      setSlideInsertRequest(null);
    }
  }, [isEditMode, iframeRef]);

  useEffect(() => {
    if (!isEditMode) return;

    requestImageOverlays();

    const onScrollResize = () => requestImageOverlays();
    window.addEventListener('scroll', onScrollResize, { passive: true });
    window.addEventListener('resize', onScrollResize, { passive: true });

    const iframe = iframeRef?.current;
    const onLoad = () => setTimeout(requestImageOverlays, 400);
    iframe?.addEventListener('load', onLoad);

    const poll = setInterval(requestImageOverlays, 2000);

    return () => {
      window.removeEventListener('scroll', onScrollResize);
      window.removeEventListener('resize', onScrollResize);
      iframe?.removeEventListener('load', onLoad);
      clearInterval(poll);
    };
  }, [isEditMode, iframeRef, requestImageOverlays]);

  const handleSaveAll = useCallback(async () => {
    if (!pendingEdits.length || isSaving) return;
    setIsSaving(true);
    setSaveStatus('saving');

    const errors = [];
    for (const edit of pendingEdits) {
      try {
        const sectionId = encodeURIComponent(edit.id);
        const res = await fetch(
          `${API_URL}/lesson/templates/${templateId}/section/${sectionId}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: authHeaders(token),
            body: JSON.stringify({ content: edit.newContent, selector: edit.selector }),
          }
        );
        if (!res.ok) { errors.push(edit.id); continue; }
        const data = await res.json();
        if (!data.success) errors.push(edit.id);
      } catch { errors.push(edit.id); }
    }

    setIsSaving(false);
    if (errors.length === 0) {
      setSaveStatus('success');
      sendToIframe(iframeRef, 'CLEAR_EDITS');
      setPendingEdits([]);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveStatus(''), 2500);
    } else {
      setSaveStatus('error');
    }
  }, [pendingEdits, isSaving, templateId, iframeRef]);

  const handleDiscard = useCallback(() => {
    sendToIframe(iframeRef, 'DISCARD');
    setPendingEdits([]);
    setSaveStatus('');
  }, [iframeRef]);

  const handleToggle = () => {
    if (isEditMode && pendingEdits.length > 0) {
      if (!window.confirm('Discard unsaved edits?')) return;
      handleDiscard();
    }
    setIsEditMode(!isEditMode);
  };

  const handleSlideInsert = useCallback(async () => {
    if (!slideInsertRequest || !slideInsertPrompt.trim()) return;
    console.log('[insert-slide] sending model:', slideInsertModel);
    setSlideInsertStatus('generating');
    setSlideInsertError('');
    try {
      const res = await fetch(
        `${API_URL}/lesson/templates/${templateId}/insert-slide`,
        {
          method: 'POST',
          credentials: 'include',
          headers: authHeaders(token),
          body: JSON.stringify({
            after_page_id: slideInsertRequest.afterPageId,
            description: slideInsertPrompt.trim(),
            slide_type: slideInsertType,
            model: slideInsertModel,   
            prev_slide_text: slideInsertRequest.prevSlideText,
            next_slide_text: slideInsertRequest.nextSlideText,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Insert failed');
      setSlideInsertRequest(null);
      setSlideInsertStatus('idle');
      setTimeout(requestImageOverlays, 1000);
    } catch (err) {
      setSlideInsertStatus('error');
      setSlideInsertError(err.message);
    }
  }, [slideInsertRequest, slideInsertPrompt, slideInsertType, templateId, requestImageOverlays]);

  const handleRegenAccept = useCallback(async () => {
    const oldUrl = regen?.originalUrl;
    const committed = await acceptRegen();
    if (committed && oldUrl) {
      sendToIframe(iframeRef, 'SWAP_IMAGE', {
        oldSrc: toRelativeSrc(oldUrl.split('?')[0]),
        newSrc: committed,
      });
      setTimeout(requestImageOverlays, 500);
    }
  }, [regen, acceptRegen, iframeRef, requestImageOverlays]);

  if (!isOwner) return null;

  return (
    <>
      <button
        onClick={handleToggle}
        className={`edit-toggle-btn ${isEditMode ? 'active' : ''}`}
        title={isEditMode ? 'Exit edit mode' : 'Click text in the lesson to edit it'}
      >
        {isEditMode ? (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Done Editing
          </>
        ) : (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit Content
          </>
        )}
      </button>

      {isEditMode && pendingEdits.length === 0 && (
        <div className="inline-edit-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Click any highlighted text in the lesson to edit
        </div>
      )}

      {isEditMode && pendingEdits.length > 0 && (
        <div className="inline-edit-save-bar">
          <span className="save-bar-count">
            {pendingEdits.length} unsaved edit{pendingEdits.length !== 1 ? 's' : ''}
          </span>
          <div className="save-bar-actions">
            <button onClick={handleDiscard} className="save-bar-btn discard" disabled={isSaving}>Discard</button>
            <button onClick={handleSaveAll} className="save-bar-btn save" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {saveStatus === 'success' && <div className="inline-edit-toast success">✓ Saved — lesson updating shortly</div>}
      {saveStatus === 'error'   && <div className="inline-edit-toast error">✗ Some edits failed to save</div>}

      {quizRequest && (
        <QuizEditor
          templateId={templateId}
          quizRequest={quizRequest}
          onClose={() => setQuizRequest(null)}
          onSaved={() => setQuizRequest(null)}
          token={token}
        />
      )}

      {imgOverlays.length > 0 && imgOverlays.map((o, i) => (
        <ImageHoverBtn
          key={`${o.src}-${i}`}
          overlay={o}
          onRegenClick={() => startRegen(o.src, o.alt, o.surroundingText)}
        />
      ))}

      {/* Slide Insert Popup */}
      {slideInsertRequest && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2147483640,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px', width: '480px', maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                Insert New Slide after "{slideInsertRequest.afterPageId}"
              </h3>
              <button
                onClick={() => setSlideInsertRequest(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#64748b' }}
              >×</button>
            </div>

            {/* Slide type selector */}
            <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Slide Type
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {[
                { value: 'content', label: '📄 Content' },
                { value: 'kc', label: '✅ KC Quiz' },
                { value: 'summary', label: '📋 Summary' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSlideInsertType(opt.value)}
                  disabled={slideInsertStatus === 'generating'}
                  style={{
                    flex: 1, padding: '8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                    cursor: 'pointer', border: '1.5px solid',
                    borderColor: slideInsertType === opt.value ? '#6366f1' : '#e2e8f0',
                    background: slideInsertType === opt.value ? '#eef2ff' : '#fff',
                    color: slideInsertType === opt.value ? '#4f46e5' : '#64748b',
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Model selector */}
<p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
  AI Model
</p>
<div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
  {[
    { value: 'claude', label: '🟣 Claude' },
    { value: 'openai', label: '🟢 GPT-5.2' },
    { value: 'gemini', label: '🔵 Gemini' },
    ...(process.env.REACT_APP_SHOW_OLLAMA_MODELS === 'true' ? [
  { value: 'ollama-qwen3.5-397b', label: 'Ollama: Qwen 3.5 397B' },
  { value: 'ollama-kimi-k2.5', label: 'Ollama: Kimi K2.5' },
  { value: 'ollama-qwen3-coder-next', label: 'Ollama: Qwen3 Coder Next' },
  { value: 'ollama-gemma4-31b', label: 'Ollama: Gemma4 31B' }
] : [])
  ].map(opt => (
    <button
      key={opt.value}
      onClick={() => setSlideInsertModel(opt.value)}
      disabled={slideInsertStatus === 'generating'}
      style={{
        flex: '1 1 30%', padding: '8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
        cursor: 'pointer', border: '1.5px solid',
        borderColor: slideInsertModel === opt.value ? '#6366f1' : '#e2e8f0',
        background: slideInsertModel === opt.value ? '#eef2ff' : '#fff',
        color: slideInsertModel === opt.value ? '#4f46e5' : '#64748b',
        transition: 'all 0.15s',
      }}
    >
      {opt.label}
    </button>
  ))}
</div>

            {/* Description */}
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
              Describe what this slide should cover.
            </p>
            <textarea
              value={slideInsertPrompt}
              onChange={e => setSlideInsertPrompt(e.target.value)}
              placeholder={
                slideInsertType === 'kc'
                  ? 'e.g. A quiz question about Riemann sums with 4 options...'
                  : slideInsertType === 'summary'
                  ? 'e.g. Summarize key integration concepts covered so far...'
                  : 'e.g. Explain the concept of limits with a visual example...'
              }
              disabled={slideInsertStatus === 'generating'}
              style={{
                width: '100%', padding: '10px', borderRadius: '8px',
                border: '1.5px solid #e2e8f0', fontSize: '14px',
                resize: 'vertical', minHeight: '80px', outline: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
              autoFocus
            />

            {slideInsertStatus === 'error' && (
              <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px' }}>{slideInsertError}</p>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSlideInsertRequest(null)}
                disabled={slideInsertStatus === 'generating'}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: '1px solid #e2e8f0',
                  background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#475569',
                }}
              >Cancel</button>
              <button
                onClick={handleSlideInsert}
                disabled={slideInsertStatus === 'generating' || !slideInsertPrompt.trim()}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none',
                  background: slideInsertStatus === 'generating' || !slideInsertPrompt.trim() ? '#94a3b8' : '#6366f1',
                  color: '#fff',
                  cursor: slideInsertStatus === 'generating' || !slideInsertPrompt.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                {slideInsertStatus === 'generating' ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Generating…
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Generate Slide
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ImageRegenPanel
        regen={regen}
        onAccept={handleRegenAccept}
        onCancel={cancelRegen}
        onRetry={retryRegen}
      />
    </>
  );
}

function ImageHoverBtn({ overlay, onRegenClick }) {
  const [hovered, setHovered] = useState(false);

  const btnSize = 28;
  const margin = 8;

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onRegenClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="Regenerate this image"
      style={{
        position: 'fixed',
        top:  overlay.top  + overlay.height - btnSize - margin,
        left: overlay.left + overlay.width  - btnSize - margin,
        width: hovered ? 'auto' : `${btnSize}px`,
        height: `${btnSize}px`,
        zIndex: 2147483646,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        padding: hovered ? '5px 10px' : '0',
        background: hovered ? 'rgba(99,102,241,0.97)' : 'rgba(17,24,39,0.45)',
        backdropFilter: 'blur(6px)',
        color: '#fff',
        border: hovered ? '1.5px solid rgba(99,102,241,0.6)' : '1.5px solid rgba(255,255,255,0.15)',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: hovered ? '0 2px 12px rgba(99,102,241,0.4)' : '0 1px 4px rgba(0,0,0,0.4)',
        transition: 'all 0.15s ease',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        pointerEvents: 'auto',
      }}
    >
      <svg
        width="11" height="11"
        viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2.5"
        style={{ flexShrink: 0 }}
      >
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
      </svg>
      {hovered && <span>Regenerate</span>}
    </button>
  );
}
